import { useMemo, type ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import {
	ArrowRight,
	CalendarDays,
	Check,
	Lock,
	MessageCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { getUserData } from '@/utils/cookies';
import { PLUMAA_ALLOW_HOME_AFTER_FORM_FLOW_KEY } from '@/constants/session';
import {
	clearSiteTutorialPending,
	hasUserSignupContext,
	markSiteTutorialPending,
} from '@/utils';
import { FORM_WELLBEING_PORTRAIT_CTA_CLASS } from './FormWellbeingPortraitMock';

const fillTemplate = (
	template: string,
	vars: Record<string, string | number>,
) =>
	template.replace(/\{(\w+)\}/g, (_, key: string) => String(vars[key] ?? ''));

export type WellbeingSavedListItem = {
	id: string;
	title: string;
	description: string;
};

export type WellbeingSavedMockData = {
	headlineTemplate: string;
	footerLine: string;
	goHomeLabel: string;
	items: WellbeingSavedListItem[];
	userNameOverride?: string;
};

export const FORM_WELLBEING_SAVED_DEFAULTS: WellbeingSavedMockData = {
	headlineTemplate: '{name}, seu Retrato de Bem-Estar foi salvo.',
	footerLine: 'Cuidar de si é o primeiro passo. 💜',
	goHomeLabel: 'Ir para o início',
	items: [
		{
			id: 'privacy',
			title: 'Dados Anonimizados',
			description:
				'Suas respostas são mantidas de forma anônima. Para a empresa, apenas indicadores agregados são exibidos.',
		},
		{
			id: 'tips',
			title: 'Dicas exclusivas',
			description:
				'Nos próximos dias, enviaremos dicas personalizadas para o seu perfil.',
		},
		{
			id: 'checkin',
			title: 'Check-in de evolução',
			description:
				'Em 30 dias, faremos um check-in rápido (5 perguntas) para ver sua evolução.',
		},
	],
};

/** Estilo por item: paleta Plumaa (continuação / dimensões). */
const ITEM_VISUAL: Record<
	string,
	{
		iconBox: string;
		icon: ReactNode;
		cardAccent: string;
	}
> = {
	privacy: {
		iconBox:
			'border border-amber-200/90 bg-gradient-to-br from-amber-50 to-orange-50/80 shadow-inner shadow-amber-900/5',
		icon: (
			<Lock
				className="size-6 text-amber-600"
				strokeWidth={2.25}
				aria-hidden
			/>
		),
		cardAccent: 'border-l-amber-500',
	},
	tips: {
		iconBox:
			'border border-brand-pink/25 bg-gradient-to-br from-[#fdf2f7] to-white shadow-inner shadow-brand-pink/10',
		icon: (
			<MessageCircle
				className="size-6 text-brand-pink"
				strokeWidth={2}
				aria-hidden
			/>
		),
		cardAccent: 'border-l-brand-pink',
	},
	checkin: {
		iconBox:
			'border border-brand-purple/25 bg-gradient-to-br from-violet-50/90 to-white shadow-inner shadow-brand-purple/10',
		icon: (
			<CalendarDays
				className="size-6 text-brand-purple"
				strokeWidth={2}
				aria-hidden
			/>
		),
		cardAccent: 'border-l-brand-purple',
	},
};

const itemBlock = (id: string) => {
	const v = ITEM_VISUAL[id];
	const box =
		'flex h-[3.25rem] w-[3.25rem] shrink-0 items-center justify-center rounded-2xl';
	if (!v) {
		return {
			iconEl: (
				<div className={cn(box, 'border border-zinc-200 bg-white')} />
			),
			cardAccent: 'border-l-zinc-300',
		};
	}
	return {
		iconEl: <div className={cn(box, v.iconBox)}>{v.icon}</div>,
		cardAccent: v.cardAccent,
	};
};

type FormWellbeingSavedMockProps = {
	data?: Partial<WellbeingSavedMockData>;
	completionRoute?: string;
};

const FormWellbeingSavedMock = ({
	data,
	completionRoute = '/home',
}: FormWellbeingSavedMockProps) => {
	const merged = { ...FORM_WELLBEING_SAVED_DEFAULTS, ...data };

	const displayName = useMemo(() => {
		const fromProp = data?.userNameOverride?.trim();
		if (fromProp) return fromProp;
		const user = getUserData();
		const first = user?.first_name?.trim();
		return first && first.length > 0 ? first : 'Você';
	}, [data?.userNameOverride]);

	const headline = useMemo(
		() =>
			fillTemplate(merged.headlineTemplate, {
				name: displayName,
			}),
		[merged.headlineTemplate, displayName],
	);

	const headlineParts = useMemo(() => {
		const idx = headline.indexOf(displayName);
		if (idx === -1) {
			return { found: false as const, headline };
		}
		return {
			found: true as const,
			before: headline.slice(0, idx),
			after: headline.slice(idx + displayName.length),
		};
	}, [headline, displayName]);

	const nextRoute = completionRoute;

	return (
		<section className="relative flex min-h-full w-full flex-col items-center bg-white px-5 py-10 sm:px-8 md:py-14 lg:px-12 xl:px-16 2xl:px-20">
			<Helmet>
				<title>Retrato salvo | Plumaa</title>
				<meta
					name="description"
					content="Seu Retrato de Bem-Estar foi salvo. Confira o que acontece a seguir."
				/>
			</Helmet>

			<div className="mx-auto flex w-full max-w-lg flex-col items-center text-center sm:max-w-2xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-6xl">
				<div
					className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500 shadow-lg shadow-emerald-600/25"
					aria-hidden
				>
					<Check className="size-11 text-white" strokeWidth={3} />
				</div>

				<h1 className="mt-8 max-w-2xl text-2xl font-semibold leading-snug tracking-tight text-zinc-900 md:text-[1.75rem] lg:text-3xl">
					{headlineParts.found ? (
						<>
							{headlineParts.before}
							<span className="text-brand-orange">
								{displayName}
							</span>
							{headlineParts.after}
						</>
					) : (
						headlineParts.headline
					)}
				</h1>

				<p className="mt-3 max-w-xl text-sm leading-relaxed text-zinc-500 md:text-base">
					Veja o que acontece a seguir com o seu bem-estar na Plumaa.
				</p>

				<ul className="mt-10 w-full space-y-4 text-left md:space-y-5">
					{merged.items.map((item) => {
						const { iconEl, cardAccent } = itemBlock(item.id);
						return (
							<li
								key={item.id}
								className={cn(
									'flex gap-4 rounded-2xl border border-zinc-200/70 bg-white/80 p-4 shadow-md shadow-zinc-900/[0.04] backdrop-blur-md transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-zinc-900/[0.06] md:gap-5 md:p-5',
									'border-l-4',
									cardAccent,
								)}
								style={{ WebkitBackdropFilter: 'blur(12px)' }}
							>
								{iconEl}
								<div className="min-w-0 flex-1 pt-0.5">
									<h2 className="text-base font-bold tracking-tight text-zinc-900 md:text-lg">
										{item.title}
									</h2>
									<p className="mt-2 text-sm leading-relaxed text-zinc-600 md:text-[0.95rem]">
										{item.description}
									</p>
								</div>
							</li>
						);
					})}
				</ul>

				<p className="mt-5 max-w-md text-center text-sm font-medium leading-relaxed text-zinc-600 md:text-base">
					{merged.footerLine}
				</p>

				<div className="mt-4 flex w-full justify-center pb-2">
					<Link
						to={nextRoute}
						onClick={() => {
							try {
								sessionStorage.setItem(
									PLUMAA_ALLOW_HOME_AFTER_FORM_FLOW_KEY,
									'1',
								);
							} catch {
								/* ignore */
							}
							if (hasUserSignupContext()) {
								markSiteTutorialPending();
							} else {
								clearSiteTutorialPending();
							}
						}}
						className={cn(
							FORM_WELLBEING_PORTRAIT_CTA_CLASS,
							'inline-flex flex-wrap items-center justify-center gap-2 whitespace-normal text-center no-underline',
						)}
					>
						{merged.goHomeLabel}
						<ArrowRight
							className="h-5 w-5 shrink-0"
							strokeWidth={2.5}
							aria-hidden
						/>
					</Link>
				</div>
			</div>
		</section>
	);
};

export { FormWellbeingSavedMock };
